import React, { useState, useMemo } from 'react';
import { useProperty } from '../hooks/useProperty';
import { useTenants } from '../hooks/useTenants';
import { useAuth } from '../hooks/useAuth';
import { seedDemoData } from '../utils/seedDemoData';
import PropertySelector from '../components/PropertySelector';
import StatsBar from '../components/StatsBar';
import ActionFeedCard from '../components/ActionFeedCard';
import AICollectionsBrief from '../components/AICollectionsBrief';
import MessageModal from '../components/MessageModal';
import BatchMessageModal from '../components/BatchMessageModal';
import { calculateERV, flattenOutreachLogs } from '../utils/ervEngine';
import { buildSentimentMap } from '../utils/sentimentAnalysis';
import { sendTenantSms } from '../utils/outreach';
import CallModal from '../components/CallModal';

export default function DashboardPage() {
  const { user } = useAuth();
  const { properties, selectedProperty, setSelectedProperty, loading: propsLoading } = useProperty();
  const { tenants, loading: tenantsLoading } = useTenants(selectedProperty?.id);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('risk');
  const [smsTarget, setSmsTarget] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedProgress, setSeedProgress] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchOpen, setBatchOpen] = useState(false);

  const handleSeedDemo = async () => {
    if (!user?.uid) {
      return;
    }
    if (!window.confirm('Add demo property with 25 realistic test tenants?')) {
      return;
    }

    setSeeding(true);
    try {
      await seedDemoData(user.uid, (message) => setSeedProgress(message));
      setSeedProgress('Done');
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      alert(`Seed failed: ${error.message}`);
      setSeeding(false);
    }
  };

  const filteredTenants = useMemo(() => {
    const next = tenants
      .filter((tenant) => {
        if (filter === 'all') return true;
        if (filter === 'high') return tenant.risk?.tier === 'high';
        if (filter === 'medium') return tenant.risk?.tier === 'medium';
        if (filter === 'low') return tenant.risk?.tier === 'low';
        if (filter === 'improving') return tenant.drift?.direction?.includes('improving');
        return true;
      })
      .filter((tenant) => {
        if (!search.trim()) return true;
        const query = search.toLowerCase();
        return tenant.name?.toLowerCase().includes(query) || tenant.unit?.toLowerCase().includes(query);
      });

    next.sort((left, right) => {
      if (sortBy === 'balance') return (right.currentBalance || 0) - (left.currentBalance || 0);
      if (sortBy === 'daysLate') return (right.currentDaysLate || 0) - (left.currentDaysLate || 0);
      return (right.risk?.score || 0) - (left.risk?.score || 0);
    });

    return next;
  }, [filter, search, sortBy, tenants]);

  const actionableTenants = filteredTenants.filter((tenant) => {
    return tenant.risk?.tier !== 'minimal' || (tenant.currentBalance && tenant.currentBalance > 0);
  });

  const ervMap = useMemo(() => {
    if (!tenants.length) {
      return new Map();
    }

    const outreachLogs = flattenOutreachLogs(tenants);
    const tenantContext = new Map(
      tenants.map((tenant) => [
        tenant.id,
        {
          daysLate: tenant.currentDaysLate || 0,
          riskScore: tenant.risk?.score || 0,
        },
      ])
    );
    const sentimentMap = buildSentimentMap(outreachLogs, tenantContext);
    const map = new Map();

    tenants.forEach((tenant) => {
      map.set(tenant.id, calculateERV(tenant, outreachLogs, sentimentMap));
    });

    return map;
  }, [tenants]);

  const toggleSelect = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === actionableTenants.length) {
      setSelectedIds(new Set());
      return;
    }

    setSelectedIds(new Set(actionableTenants.map((tenant) => tenant.id)));
  };

  const selectedTenants = actionableTenants.filter((tenant) => selectedIds.has(tenant.id));
  const allSelected = actionableTenants.length > 0 && selectedIds.size === actionableTenants.length;

  const handleSendSMS = async (tenant, message, templateUsed, lang) => {
    await sendTenantSms({
      tenant,
      propertyId: selectedProperty?.id,
      message,
      templateKey: templateUsed,
      lang,
    });
  };

  const handleBatchSend = async (tenant, message, templateUsed, lang) => {
    await sendTenantSms({
      tenant,
      propertyId: selectedProperty?.id,
      message,
      templateKey: templateUsed,
      lang,
    });
  };

  const loading = propsLoading || tenantsLoading;
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const counts = {
    all: tenants.length,
    high: tenants.filter((tenant) => tenant.risk?.tier === 'high').length,
    medium: tenants.filter((tenant) => tenant.risk?.tier === 'medium').length,
    low: tenants.filter((tenant) => tenant.risk?.tier === 'low').length,
    improving: tenants.filter((tenant) => tenant.drift?.direction?.includes('improving')).length,
  };
  const summaryCards = [
    {
      label: 'Action Queue',
      value: actionableTenants.length,
      sub: `${selectedProperty?.name || 'Portfolio'} needs review today`,
      tone: 'bg-blue-50 border-blue-200 text-blue-700',
    },
    {
      label: 'Autopilot Ready',
      value: tenants.filter((tenant) => (tenant.currentBalance || 0) > 0 && !!tenant.phone).length,
      sub: 'Delinquent tenants with a phone on file',
      tone: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    },
    {
      label: 'Active Plans',
      value: tenants.filter((tenant) => tenant.paymentPlan?.status === 'active').length,
      sub: 'Payment plans currently being tracked',
      tone: 'bg-amber-50 border-amber-200 text-amber-700',
    },
  ];

  if (!loading && properties.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-4xl font-bold text-tenafi-green mb-4">Start</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Tenafi</h2>
          <p className="text-gray-500 mb-6">Upload your first rent roll, or seed demo data to see the platform in action.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/upload"
              className="px-6 py-3 bg-tenafi-green text-white rounded-xl font-semibold hover:bg-tenafi-green-dark transition-colors text-center"
            >
              Upload Property Data
            </a>
            <button
              onClick={handleSeedDemo}
              disabled={seeding}
              className="px-6 py-3 bg-tenafi-green text-white rounded-xl font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-60"
            >
              {seeding ? seedProgress || 'Seeding...' : 'Load Demo Data'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI CFO Daily Briefing</h1>
            <p className="text-sm text-gray-500 mt-0.5">{today}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <PropertySelector properties={properties} selected={selectedProperty} onSelect={setSelectedProperty} />
            <a
              href="/upload"
              className="px-3 py-2.5 text-xs font-medium text-white bg-tenafi-green rounded-xl hover:bg-tenafi-green-dark transition-colors"
            >
              + Upload
            </a>
            <button
              onClick={handleSeedDemo}
              disabled={seeding}
              className="px-3 py-2.5 text-xs font-medium text-gray-500 border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50"
            >
              {seeding ? seedProgress || '...' : 'Seed Demo'}
            </button>
          </div>
        </div>

        {!loading && <StatsBar tenants={tenants} />}

        {!loading && (
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {summaryCards.map((card) => (
              <div key={card.label} className={`rounded-2xl border p-4 ${card.tone}`}>
                <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{card.label}</p>
                <p className="text-2xl font-bold mt-1">{card.value}</p>
                <p className="text-sm opacity-80 mt-1">{card.sub}</p>
              </div>
            ))}
          </div>
        )}

        {!loading && selectedProperty && tenants.length > 0 && (
          <AICollectionsBrief
            property={selectedProperty}
            tenants={tenants}
            ervMap={ervMap}
          />
        )}

        {/* Search + sort row */}
        <div className="flex gap-2 mt-6 mb-2">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name or unit…"
            className="flex-1 min-w-0 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
          />
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            className="shrink-0 border border-gray-300 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:ring-2 focus:ring-tenafi-green outline-none"
          >
            <option value="risk">Risk</option>
            <option value="balance">Balance</option>
            <option value="daysLate">Days late</option>
          </select>
        </div>

        {/* Filter pills — horizontal scroll on mobile */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {[
            { key: 'all', label: 'All', dot: null },
            { key: 'high', label: 'High Risk', dot: 'bg-red-500' },
            { key: 'medium', label: 'Medium', dot: 'bg-amber-500' },
            { key: 'low', label: 'Low Risk', dot: 'bg-gray-400' },
            { key: 'improving', label: 'Improving', dot: 'bg-green-500' },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key)}
              className={
                'flex shrink-0 items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-colors min-h-[36px] ' +
                (filter === item.key ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
              }
            >
              {item.dot && <span className={`w-1.5 h-1.5 rounded-full ${filter === item.key ? 'bg-white' : item.dot}`} />}
              {item.label} <span className="opacity-75">({counts[item.key]})</span>
            </button>
          ))}
          {(filter !== 'all' || search) && (
            <button
              onClick={() => { setFilter('all'); setSearch(''); }}
              className="shrink-0 px-3 py-2 rounded-full text-xs font-medium text-tenafi-green bg-tenafi-light min-h-[36px]"
            >
              Clear ✕
            </button>
          )}
        </div>

        {!loading && actionableTenants.length > 0 && (
          <div className="flex items-center gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="accent-tenafi-green w-4 h-4"
              />
              {allSelected ? 'Deselect all' : 'Select all'}
            </label>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setBatchOpen(true)}
                className="px-4 py-1.5 bg-tenafi-amber text-white rounded-xl text-sm font-semibold hover:bg-tenafi-amber-dark transition-colors"
              >
                Send to {selectedIds.size} Selected
              </button>
            )}
            {selectedIds.size > 0 && (
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((item) => (
              <div key={item} className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse">
                <div className="flex gap-3 mb-3">
                  <div className="h-5 bg-gray-200 rounded w-48" />
                  <div className="h-5 bg-gray-200 rounded w-24" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : actionableTenants.length === 0 && filter === 'all' && !search ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <div className="text-3xl font-bold text-tenafi-green mb-3">Clear</div>
            <p className="text-gray-700 font-medium">All clear. No tenants need attention.</p>
            <button
              onClick={handleSeedDemo}
              disabled={seeding}
              className="mt-4 px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark disabled:opacity-50"
            >
              {seeding ? 'Seeding...' : 'Load 25 Demo Tenants'}
            </button>
          </div>
        ) : actionableTenants.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
            <div className="text-3xl font-bold text-tenafi-green mb-2">Search</div>
            <p className="text-gray-600 font-medium">No tenants match this filter.</p>
            <button
              onClick={() => {
                setFilter('all');
                setSearch('');
              }}
              className="mt-3 text-tenafi-green text-sm hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {actionableTenants.map((tenant) => (
              <ActionFeedCard
                key={tenant.id}
                tenant={tenant}
                propertyId={selectedProperty?.id}
                propertyName={selectedProperty?.name}
                isSelected={selectedIds.has(tenant.id)}
                onToggleSelect={() => toggleSelect(tenant.id)}
                onSendSMS={(nextTenant) => setSmsTarget({ tenant: nextTenant, mode: 'sms' })}
                onOfferPlan={(nextTenant) => setSmsTarget({ tenant: nextTenant, mode: 'plan' })}
                onCallTenant={(nextTenant) => setSmsTarget({ tenant: nextTenant, mode: 'call' })}
                erv={ervMap.get(tenant.id)}
              />
            ))}
          </div>
        )}
      </div>

      {smsTarget && smsTarget.mode === 'call' && (
        <CallModal
          tenant={smsTarget.tenant}
          propertyId={selectedProperty?.id}
          propertyName={selectedProperty?.name}
          onClose={() => setSmsTarget(null)}
          onCallLogged={() => setSmsTarget(null)}
        />
      )}

      {smsTarget && smsTarget.mode !== 'call' && (
        <MessageModal
          tenant={smsTarget.tenant}
          initialMode={smsTarget.mode}
          propertyName={selectedProperty?.name}
          onClose={() => setSmsTarget(null)}
          onSend={handleSendSMS}
        />
      )}

      {batchOpen && (
        <BatchMessageModal
          tenants={selectedTenants}
          propertyName={selectedProperty?.name}
          onClose={() => {
            setBatchOpen(false);
            setSelectedIds(new Set());
          }}
          onSend={handleBatchSend}
        />
      )}
    </div>
  );
}
